import { useRouter } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";

import { fontFamily } from "@passfirst/ui";

import { type Colors, useColors } from "../src/theme";
import { trpc } from "../src/trpc";
import { useResponsive } from "../src/use-responsive";
import { useSelectedVertical } from "../src/use-selected-vertical";
import { useVoiceCoach } from "../src/voice/use-voice-coach";
import { useVoiceSessionEvents } from "../src/voice/use-voice-session-events";
import { VoiceBottomBar } from "../src/voice/voice-bottom-bar";
import { VoiceOrb } from "../src/voice/voice-orb";
import { VoiceTopBar } from "../src/voice/voice-top-bar";
import { VoiceWaveform } from "../src/voice/voice-waveform";

// Voice coach screen. Full navy ground with a coral orb that pulses while
// the mic is open. Real on-device pipeline: STT → voice.coach LLM → TTS
// (see use-voice-coach). Gated on the vertical's voiceMode capability so a
// credential without a voice coach never reaches the live mic.
export default function VoiceCoach(): JSX.Element {
  const router = useRouter();
  const vertical = useSelectedVertical();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();

  useVoiceSessionEvents(vertical.verticalSlug ?? null);

  // Capability gate — confirm this credential actually has a voice coach
  // before opening the mic. null = still resolving.
  const [voiceMode, setVoiceMode] = useState<boolean | null>(null);
  useEffect(() => {
    if (vertical.isLoading) return;
    if (!vertical.verticalSlug) {
      setVoiceMode(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const v = await trpc.vertical.bySlug.query({
          slug: vertical.verticalSlug!,
        });
        if (!cancelled) setVoiceMode(v?.capabilities?.voiceMode === true);
      } catch {
        if (!cancelled) setVoiceMode(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vertical.isLoading, vertical.verticalSlug]);

  const coach = useVoiceCoach({
    verticalSlug: vertical.verticalSlug,
    jurisdictionCode: vertical.jurisdictionCode,
  });

  // Auto-start once we've confirmed the capability (the user navigated here
  // deliberately). Permission is requested inside start().
  const startedRef = useRef(false);
  const startCoach = coach.start;
  useEffect(() => {
    if (voiceMode === true && !startedRef.current) {
      startedRef.current = true;
      startCoach();
    }
  }, [voiceMode, startCoach]);

  const goBack = () => {
    coach.end();
    if (router.canGoBack()) router.back();
    else router.replace("/home");
  };

  const coachName = vertical.verticalSlug ? "Practice Coach" : "Coach";
  const coachSub = vertical.verticalSlug
    ? "Voice mode · adaptive"
    : "Voice mode";

  // ---- Gate states --------------------------------------------------------
  if (voiceMode === null) {
    return (
      <View style={[styles.outer, styles.center]}>
        <ActivityIndicator color="#FFFFFF" />
      </View>
    );
  }
  if (voiceMode === false) {
    return (
      <View style={styles.outer}>
        <VoiceTopBar coachName={coachName} coachSub={coachSub} onClose={goBack} />
        <View style={styles.center}>
          <Text style={styles.gateTitle}>Voice coaching isn&apos;t available</Text>
          <Text style={styles.gateBody}>
            {vertical.verticalSlug
              ? "This credential doesn't have a voice coach yet — we add them credential by credential."
              : "Pick a credential first, then come back to practice out loud."}
          </Text>
          <Pressable
            onPress={goBack}
            style={styles.gateButton}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <Text style={styles.gateButtonText}>Back</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  // ---- Live coach ---------------------------------------------------------
  const mainText =
    coach.state === "speaking" || coach.state === "thinking"
      ? coach.coachText || "…"
      : coach.userText ||
        "Speak when you're ready — I'll coach you through it out loud.";

  const statusText: string = (() => {
    switch (coach.state) {
      case "listening":
        return "Listening — speak naturally";
      case "thinking":
        return "Thinking…";
      case "speaking":
        return "Coach is speaking…";
      case "paused":
        return coach.error ?? "Paused — tap the mic to resume";
      case "denied":
        return "Microphone access is off. Enable it in Settings, then tap the mic.";
      case "unsupported":
        return "Voice coaching isn't supported on this device.";
      case "error":
        return coach.error ?? "Something went wrong. Tap the mic to retry.";
      default:
        return "Getting ready…";
    }
  })();

  return (
    <View style={styles.outer}>
      <VoiceTopBar coachName={coachName} coachSub={coachSub} onClose={goBack} />

      <View style={styles.content}>
        <VoiceOrb listening={coach.capturing} size={isWide ? 144 : 120} />
        <VoiceWaveform listening={coach.capturing} />
        <View style={styles.transcriptBlock}>
          <Text style={[styles.transcript, isWide && styles.transcriptWide]}>
            {mainText}
          </Text>
          <Text style={styles.transcriptSub} accessibilityLiveRegion="polite">
            {statusText}
          </Text>
        </View>
      </View>

      <VoiceBottomBar
        listening={coach.active && coach.state !== "paused"}
        onTogglePause={
          coach.state === "denied" || coach.state === "unsupported"
            ? coach.start
            : coach.togglePause
        }
        onEnd={goBack}
      />
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    outer: { flex: 1, backgroundColor: colors.surfaceImmersive },
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 32,
      gap: 14,
    },
    content: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 26,
      paddingVertical: 24,
      gap: 22,
    },
    transcriptBlock: {
      maxWidth: 30 * 8,
      alignItems: "center",
      gap: 14,
    },
    transcript: {
      fontFamily: fontFamily.semiBold,
      fontSize: 20,
      lineHeight: 29,
      color: "#FFFFFF",
      textAlign: "center",
    },
    transcriptWide: { fontSize: 26, lineHeight: 36 },
    transcriptSub: {
      fontFamily: fontFamily.regular,
      fontSize: 14,
      color: "#9FB2CC",
      textAlign: "center",
    },
    gateTitle: {
      fontFamily: fontFamily.bold,
      fontSize: 20,
      color: "#FFFFFF",
      textAlign: "center",
    },
    gateBody: {
      fontFamily: fontFamily.regular,
      fontSize: 15,
      lineHeight: 22,
      color: "#9FB2CC",
      textAlign: "center",
    },
    gateButton: {
      marginTop: 8,
      backgroundColor: "#FFFFFF",
      paddingHorizontal: 28,
      paddingVertical: 13,
      borderRadius: 26,
    },
    gateButtonText: {
      fontFamily: fontFamily.bold,
      fontSize: 15,
      color: colors.brand,
    },
  });
}
